import Dialog from '@mui/material/Dialog';
import { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import ThemeConstants from '../../../../config/theme/theme_constants';
import { useCommentSurvey } from '../../domain/hooks/add_survey_comment';
import { PulseLoader } from 'react-spinners';
import { useModifySurveyComment } from '../../domain/hooks/modify_survey_comment';
import { useTranslation } from '../../../../i18n/useTranslation';

interface OwnQuestionDialogProps {
    onValidate: (comment?: string | null) => void;
    onClose: () => void;
    open: boolean;
    initialComment?: string;
    surveyId: string;
}

const isQuestionValid = (question: string): boolean => {
    return question.trim().length > 0;
}

const SurveyCommentDialog: React.FC<OwnQuestionDialogProps> = ({ onValidate, onClose, open, initialComment, surveyId }) => {
    const { t } = useTranslation();
    const theme = ThemeConstants();
    const [ comment, setComment ] = useState<string>(initialComment || "");
    const [ isValid, setIsValid ] = useState<boolean>(isQuestionValid(initialComment || ""));
    const { mutate: createComment, isPending: isCreatingComment } = useCommentSurvey();
    const { mutate: modifyComment, isPending: isModifyingComment } = useModifySurveyComment();

    useEffect(() => {
        const questionValid = isQuestionValid(comment);
        if (questionValid !== isValid) {
            setIsValid(questionValid);
        }
    }, [comment, isValid]);

    const addComment = useCallback(() => {
        if (!isValid || isCreatingComment || isModifyingComment) return;
        if (initialComment === comment) {
            onValidate();
            return;
        }
        const handlers = {
            onSuccess: (data: string | null) => onValidate(data),
            onError: (error: AxiosError) => {
                const data = error.response?.data as { message?: string } | undefined;
                toast.error(t('survey.error_with_message', { message: data?.message ?? t('common.error.unknown') }));
            },
        };
        if (initialComment) {
            modifyComment({ surveyId, comment }, handlers);
        } else {
            createComment({ surveyId, comment }, handlers);
        }
    }, [isValid, isCreatingComment, isModifyingComment, createComment, surveyId, comment, initialComment, modifyComment, onValidate, t]);

    return (
        <Dialog
            fullWidth={true}
            onClose={onClose}
            open={open}
            slotProps={{
                backdrop: {
                    style: {
                        backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    },
                },
                container: {
                    className: '!w-screen'
                },
                paper: {
                    className: '!rounded-[20px] !m-3 !w-full',
                },
            }}
        >
            <div className='flex flex-col gap-5 w-full bg-white px-5 py-4.5'>
                <button
                    className="cursor-pointer p-1"
                    onClick={onClose}
                >
                    <svg 
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <g>
                            <path d="M12 13.3998L7.1 18.2998C6.91667 18.4831 6.68334 18.5748 6.4 18.5748C6.11667 18.5748 5.88334 18.4831 5.7 18.2998C5.51667 18.1165 5.425 17.8831 5.425 17.5998C5.425 17.3165 5.51667 17.0831 5.7 16.8998L10.6 11.9998L5.7 7.0998C5.51667 6.91647 5.425 6.68314 5.425 6.3998C5.425 6.11647 5.51667 5.88314 5.7 5.6998C5.88334 5.51647 6.11667 5.4248 6.4 5.4248C6.68334 5.4248 6.91667 5.51647 7.1 5.6998L12 10.5998L16.9 5.6998C17.0833 5.51647 17.3167 5.4248 17.6 5.4248C17.8833 5.4248 18.1167 5.51647 18.3 5.6998C18.4833 5.88314 18.575 6.11647 18.575 6.3998C18.575 6.68314 18.4833 6.91647 18.3 7.0998L13.4 11.9998L18.3 16.8998C18.4833 17.0831 18.575 17.3165 18.575 17.5998C18.575 17.8831 18.4833 18.1165 18.3 18.2998C18.1167 18.4831 17.8833 18.5748 17.6 18.5748C17.3167 18.5748 17.0833 18.4831 16.9 18.2998L12 13.3998Z" fill="black"/>
                        </g>
                    </svg>
                </button>
                <h2 className='text-black text-xl font-medium leading-5 text-center w-full'>{t('survey.comment.title')}</h2>
                <div className='flex flex-col w-full items-center gap-5'>
                    <textarea
                        id="survey-comment"
                        placeholder={t('survey.comment.placeholder')}
                        value={comment}
                        onChange={(e) => {
                            if (!isCreatingComment && !isModifyingComment) {
                                setComment(e.target.value)
                            }
                        }}
                        className='w-full h-[30dvh] p-4 bg-gray-300 rounded-[10px] focus:outline-none resize-none'
                    />

                    <button
                        disabled={!isValid || isCreatingComment || isModifyingComment}
                        className={`rounded-full font-bold w-full max-w-63 px-9.25 py-2.5 transition-colors duration-200 text-center ${
                            !isValid || isCreatingComment || isModifyingComment ? 'cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        onClick={addComment}
                        style={isValid ? { backgroundColor: theme.UvibesPink, color: 'white' } : { backgroundColor: '#494949', color: '#858585' }}
                        >
                        {isCreatingComment || isModifyingComment ? <PulseLoader speedMultiplier={0.75} size={8} color="white"/> : initialComment !== undefined ? t('survey.comment.edit') : t('survey.comment.send')}
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

export default SurveyCommentDialog;